import { extractDate } from "fileParsing/wrangler/extractTemplates";
import {
  DataPointTemplate,
  DataTemplate,
  DateParserArguments,
  EMPTY_SCHEMA,
  Path,
  UserInteractionStatus,
  Visibility,
  WranglerItem,
} from "fileParsing/wrangler/types";
import { Add as AddIcon } from "@mui/icons-material";
import { Box, Grid, Modal, Typography, useTheme } from "@mui/material";
import { ApplicationState } from "@store/index";
import { updateData, updateStatus } from "@store/wrangler/actions";
import { useProviderFiles } from "containers/hooks/useData";
import { useAppDispatch, useAppSelector } from "index";
import React, { useMemo, useRef, useState } from "react";
import { getExportWorkerByUUID } from "webworker/init";
import DataCard from "./DataCard";
import FileTree from "./FileTree";
import { PlaintextView } from "./PlaintextView";
import Preview from "./Preview";
import TimestampArgumentModal from "./TimestampArgumentModal";

type Props = {
  selection?: WranglerItem;
};

const MapView: React.FC<Props> = ({
  selection = {
    id: -1,
    name: "",
    exportId: -1,
    schema: EMPTY_SCHEMA,
    templates: [],

    category: undefined,
    userStatus: UserInteractionStatus.Automatic,
    dataProgress: {
      didCompile: false,
      foundDataPoints: false,
      guessedCategory: false,
      hasTimestamp: false,
      isNotEmpty: false,
    },

    files: [],
  },
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const providerItems = useAppSelector((s: ApplicationState) => s.data.exports);
  const providerFiles = useProviderFiles(selection?.exportId ?? -1);
  const wranglerItems = useAppSelector((s: ApplicationState) => s.wrangler.items);
  const wranglerItem = wranglerItems.find(({ id }) => id === selection.id);

  const templates = wranglerItem?.templates ?? ([] as DataPointTemplate[]);

  const debounce = useRef<NodeJS.Timeout>();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openTab, setOpenTab] = useState<"json" | "plain">("json");
  const [hovered, setHovered] = useState<string | undefined>();
  const [timestamp2Format, setTimestamp2Format] = useState<
    (DataTemplate & { template: Path; arguments?: DateParserArguments }) | undefined
  >();
  const [previewUri, setPreviewUri] = useState<Path | undefined>();

  const [itemFiles, samples] = useMemo(() => {
    const files = providerFiles.filter(({ id }) => selection.files.some((sel) => id === sel.id));

    const samples = files.reduce<unknown[]>(
      (arr, f) => [...arr, ...(f.parserData.structure ? [f.parserData.structure] : [])],
      [],
    );

    return [files, samples];
  }, [providerItems.length, providerFiles, selection.files]);

  function applyModification(path: Path, mod: (d: DataPointTemplate) => Partial<DataPointTemplate>): void {
    const tmplIx = templates.findIndex((t) => t.path.toString() === path.toString());

    if (tmplIx > -1) {
      const tmpl = templates[tmplIx];
      const t = { ...tmpl, ...mod(tmpl), updated: true };

      const visibleTemplates = Object.assign<
        DataPointTemplate[],
        DataPointTemplate[],
        { [ix: number]: DataPointTemplate }
      >([], templates, {
        [tmplIx]: t,
      }).filter((template) => template.visibility !== Visibility.hidden);

      dispatch(
        updateStatus(selection.id, UserInteractionStatus.Edited, {
          foundDataPoints: visibleTemplates.length > 0,
          hasTimestamp: visibleTemplates.some((template) => template.selectedTimestamps.length > 0),
          guessedCategory: visibleTemplates.some((template) => template.category !== undefined),
        }),
      );

      dispatch(updateData(selection.id, t));

      // Debounce and auto-save
      clearTimeout(debounce.current);
      debounce.current = setTimeout(() => compileDataPoints(visibleTemplates), 1000);
    }
  }

  const setTemplateVisibility = ([templatePath, templateVis]: [Path, Visibility]) => {
    applyModification(templatePath, () => ({ visibility: templateVis }));
  };

  const compileDataPoints = async (templates: DataPointTemplate[]) => {
    // Current will not include files from ZIP's loaded after files selected
    // -> but ProviderItems will

    const workerId = providerItems.find(({ id }) => id === selection.exportId)?.workerId;
    if (workerId === undefined) return;

    const worker = await getExportWorkerByUUID(workerId)?.getWrangler();
    if (worker === undefined) return;

    const didCompile = worker.compileNewTemplates({
      ...selection,
      templates,
    });

    if (
      wranglerItem?.userStatus !== UserInteractionStatus.Automatic &&
      wranglerItem?.userStatus !== UserInteractionStatus.Resolved
    ) {
      dispatch(
        updateStatus(
          selection.id,
          UserInteractionStatus.Compiled,
          {
            didCompile,
          },
          true,
        ),
      );
    } else {
      dispatch(
        updateStatus(
          selection.id,
          UserInteractionStatus.Resolved,
          {
            didCompile,
          },
          true,
        ),
      );
    }
  };

  return (
    <>
      <Grid container sx={{ width: "100%", height: "100%" }}>
        <Grid item xs={5} sx={{ height: "100%", overflowX: "hidden", p: 1 }}>
          <Typography variant="h3">Source Data Structure</Typography>
          {/* <Tabs
            value={openTab}
            onChange={(_, v) => setOpenTab(v as "json" | "plain")}
          >
            <Tab label="JSON View" value="json" />
            <Tab label="Plaintext" value="plain" />
          </Tabs> */}
          {openTab === "json" ? (
            <FileTree
              tree={selection.schema}
              files={itemFiles}
              selected={(
                templates
                  .filter(({ visibility }) => visibility === Visibility.visible)
                  .map((t) => [
                    ...t.subcategory.filter((c) => c.type === "data").map((c) => c.value as Path),
                    ...t.content.filter((c) => c.type === "data").map((c) => c.value as Path),
                    ...t.selectedTimestamps.map((s) => s.path),
                    ...t.selectedUris,
                    ...t.selectedContacts,
                    ...t.selectedLatitudes,
                    ...t.selectedLongitudes,
                  ]) ?? []
              ).reduce<Path[]>((flat, curr) => [...flat, ...curr], [])}
              hovered={hovered}
              setHovered={(path) => setHovered(path?.toString())}
              visibilities={templates.map(({ path, visibility }) => [path, visibility])}
              onChange={setTemplateVisibility}
            />
          ) : (
            <PlaintextView files={itemFiles} />
          )}
        </Grid>
        <Grid
          item
          xs={4}
          sx={{
            position: "relative",
            height: "100%",
            overflowX: "hidden",
            p: 1,
          }}
        >
          <Typography variant="h3">Target Data Structure</Typography>
          {templates
            // hide hidden templates
            .filter((template) => template.visibility !== Visibility.hidden)
            .map((template) => (
              <DataCard
                key={`${selection.name}-${itemFiles.length}-${template.path.toString()}`}
                template={template}
                data={samples} /* Sample file contents */
                onChange={(t) => applyModification(template.path, () => t)}
                onFormatTimestamp={setTimestamp2Format}
                hovered={hovered}
                setHovered={(path) => setHovered(path?.toString())}
                previewUri={setPreviewUri}
              />
            ))}
          {templates.filter((template) => template.visibility !== Visibility.hidden).length === 0 ? (
            <h2
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                textAlign: "center",
                transform: "translate(-50%, -50%)",
              }}
            >
              Select data using{" "}
              <span
                style={{
                  padding: theme.spacing(1),
                  border: "1px solid #fff",
                  borderRadius: 5,
                }}
              >
                <AddIcon />
              </span>{" "}
              in the center
            </h2>
          ) : null}
        </Grid>
        <Grid
          item
          xs={3}
          sx={{
            position: "relative",
            height: "100%",
            overflowX: "hidden",
            padding: 1,
          }}
        >
          <Typography variant="h3">Verification Panel</Typography>
          <Preview files={itemFiles} />
        </Grid>
      </Grid>
      <Modal open={timestamp2Format !== undefined}>
        {timestamp2Format !== undefined ? (
          <TimestampArgumentModal
            format={timestamp2Format}
            onCancel={() => setTimestamp2Format(undefined)}
            samples={itemFiles.reduce<(string | number)[]>((result, f) => {
              const extracted = extractDate(
                f.parserData?.structure,
                Array.from(timestamp2Format?.value as Path),
              ).filter<number | string>(
                (value): value is number | string => typeof value === "number" || typeof value === "string",
              );
              return [...result, ...extracted];
            }, [])}
            onConfirm={(format) => {
              // Check if timestamp needs to be appended or modified
              if (timestamp2Format.arguments) {
                // Update
                applyModification(timestamp2Format.template, (d) => {
                  const index = d.selectedTimestamps.findIndex((t) => t.path.toString() === format.value.toString());
                  return {
                    selectedTimestamps: Object.assign([], d.selectedTimestamps, {
                      [index]: {
                        path: format.value,
                        arguments: format.arguments,
                      },
                    }),
                  };
                });
              } else if (format.arguments !== undefined) {
                // Append
                applyModification(timestamp2Format.template, (d) => ({
                  ...d,
                  selectedTimestamps: d.selectedTimestamps.concat({
                    path: format.value as Path,
                    arguments: format.arguments as DateParserArguments,
                  }),
                }));
              }
              setTimestamp2Format(undefined);
            }}
          />
        ) : (
          <></>
        )}
      </Modal>
      <Modal open={previewUri !== undefined}>
        <Box
          onClick={() => setPreviewUri(undefined)}
          sx={{
            width: "100vw",
            height: "100vh",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            overflowY: "scroll",
          }}
        >
          {previewUri &&
            itemFiles
              .map((f) => {
                // previewUri contains the position of the
                // uris in the data (ex. JSON) files
                // Extract the actual image uris from the data
                const uri = extractDate(f.parserData?.structure, previewUri);
                // Assume: Only file(s) from one providerItem are selected
                // (even if multiple similar files are merged together?)

                if (providerFiles) {
                  // Search image uri in providerfiles and return image
                  const image = providerFiles.find((f) => f.path === uri[0]);
                  if (image) {
                    return image.binaryData;
                  }
                }
                return null;
              })
              .filter<string>((img): img is string => img !== null)
              .map((img) => (
                <img
                  alt="Preview"
                  onClick={() => setPreviewUri(undefined)}
                  key={`preview-${img.substring(10, 100)}`}
                  style={{
                    maxWidth: "90vw",
                    maxHeight: "90vh",
                    objectFit: "contain",
                    display: "block",
                    margin: "10px auto",
                  }}
                  src={img}
                />
              ))}
        </Box>
      </Modal>
    </>
  );
};

export default MapView;
